import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Pencil, X, Building2, Mail, Phone, MapPin, Save, User, Hash, FileText, Key, Copy, ChevronDown, ChevronRight, Trash2, UserPlus, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function ContactRow({ contact, clientId, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: contact.name, email: contact.email, phone: contact.phone, role: contact.role });

  const save = async () => {
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`${API}/api/clients/${clientId}/contacts/${contact.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (!r.ok) { toast.error('Update failed'); return; }
      toast.success('Contact updated');
      setEditing(false);
      onUpdate();
    } catch { toast.error('Update failed'); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${contact.name}?`)) return;
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API}/api/clients/${clientId}/contacts/${contact.id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Contact removed');
      onDelete();
    } catch { toast.error('Delete failed'); }
  };

  if (editing) {
    return (
      <tr className="bg-muted/5">
        <td className="p-2 pl-12"><Input data-testid={`edit-contact-name-${contact.id}`} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Name" className="h-8 text-sm" /></td>
        <td className="p-2"><Input value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} placeholder="Role" className="h-8 text-sm" /></td>
        <td className="p-2"><Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="h-8 text-sm" /></td>
        <td className="p-2"><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="h-8 text-sm" /></td>
        <td className="p-2">
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={save}><Save className="h-3 w-3" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(false)}><X className="h-3 w-3" /></Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-muted/5 hover:bg-muted/10 transition-colors">
      <td className="p-2 pl-12 text-sm">
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5 text-muted-foreground" />
          {contact.name}
        </div>
      </td>
      <td className="p-2 text-sm text-muted-foreground">{contact.role}</td>
      <td className="p-2 text-sm">{contact.email && <a href={`mailto:${contact.email}`} className="text-blue-400 hover:underline">{contact.email}</a>}</td>
      <td className="p-2 text-sm text-muted-foreground">{contact.phone}</td>
      <td className="p-2">
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(true)} data-testid={`edit-contact-${contact.id}`}><Pencil className="h-3 w-3" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={remove} data-testid={`delete-contact-${contact.id}`}><Trash2 className="h-3 w-3" /></Button>
        </div>
      </td>
    </tr>
  );
}

function AddContactRow({ clientId, onAdded, onCancel }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '' });

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`${API}/api/clients/${clientId}/contacts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (!r.ok) { toast.error('Add failed'); return; }
      toast.success('Contact added');
      onAdded();
    } catch { toast.error('Add failed'); }
  };

  return (
    <tr className="bg-muted/5">
      <td className="p-2 pl-12"><Input data-testid="new-contact-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Name *" className="h-8 text-sm" autoFocus /></td>
      <td className="p-2"><Input data-testid="new-contact-role" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} placeholder="Role / Title" className="h-8 text-sm" /></td>
      <td className="p-2"><Input data-testid="new-contact-email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="h-8 text-sm" /></td>
      <td className="p-2"><Input data-testid="new-contact-phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="h-8 text-sm" /></td>
      <td className="p-2">
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={save} data-testid="save-new-contact"><Save className="h-3 w-3" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCancel}><X className="h-3 w-3" /></Button>
        </div>
      </td>
    </tr>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [addingContact, setAddingContact] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', city: '', province: '', postal_code: '', email: '', phone: '', contact_name: '', job_number: '', rig_specific: '', others: '', notes: '' });

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const fetchClients = useCallback(async () => {
    try {
      const t = localStorage.getItem('token');
      const r = await fetch(`${API}/api/clients?search=${encodeURIComponent(search)}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` }
      });
      const d = await r.json();
      if (d.success) setClients(d.clients);
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      const url = editing ? `${API}/api/clients/${editing}` : `${API}/api/clients`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Error'); return; }
      toast.success(editing ? 'Client updated' : 'Client created');
      setEditing(null); setCreating(false);
      setForm({ name: '', address: '', city: '', province: '', postal_code: '', email: '', phone: '', contact_name: '', job_number: '', rig_specific: '', others: '', notes: '' });
      fetchClients();
    } catch { toast.error('Save failed'); }
  };

  const handleDeleteClient = async (clientId, name) => {
    if (!window.confirm(`Delete client "${name}"? This will remove all their contacts too.`)) return;
    try {
      const r = await fetch(`${API}/api/clients/${clientId}`, { method: 'DELETE', headers });
      if (!r.ok) { toast.error('Delete failed'); return; }
      toast.success('Client deleted');
      fetchClients();
    } catch { toast.error('Delete failed'); }
  };

  const startEdit = (c) => {
    setEditing(c.client_id);
    setCreating(false);
    setForm({ name: c.name || '', address: c.address || '', city: c.city || '', province: c.province || '', postal_code: c.postal_code || '', email: c.email || '', phone: c.phone || '', contact_name: c.contact_name || '', job_number: c.job_number || '', rig_specific: c.rig_specific || '', others: c.others || '', notes: c.notes || '' });
  };

  const startCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm({ name: '', address: '', city: '', province: '', postal_code: '', email: '', phone: '', rig_specific: '', others: '' });
  };

  const cancel = () => { setEditing(null); setCreating(false); };

  const toggleExpand = (clientId) => {
    setExpanded(p => ({ ...p, [clientId]: !p[clientId] }));
    setAddingContact(null);
  };

  const formFields = [
    { key: 'name', label: 'Company Name', icon: Building2, required: true },
    { key: 'contact_name', label: 'Primary Contact', icon: User },
    { key: 'address', label: 'Address', icon: MapPin },
    { key: 'city', label: 'City', icon: MapPin },
    { key: 'province', label: 'Province', icon: MapPin },
    { key: 'postal_code', label: 'Postal Code', icon: MapPin },
    { key: 'email', label: 'Email', icon: Mail },
    { key: 'phone', label: 'Phone', icon: Phone },
    { key: 'job_number', label: 'Job #', icon: Hash },
    { key: 'rig_specific', label: 'Rig Specific', icon: Building2 },
    { key: 'others', label: 'Others', icon: Building2 },
    { key: 'notes', label: 'Notes', icon: FileText, multiline: true },
  ];

  return (
    <div data-testid="clients-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Clients</h1>
        <Button data-testid="add-client-btn" onClick={startCreate} size="sm"><Plus className="h-4 w-4 mr-1" />Add Client</Button>
      </div>

      {(creating || editing) && (
        <div data-testid="client-form" className="bg-card border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">{editing ? 'Edit Client' : 'New Client'}</h2>
            <Button variant="ghost" size="icon" onClick={cancel}><X className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {formFields.map(f => (
              <div key={f.key}>
                <label className="text-xs text-muted-foreground mb-1 block">{f.label}{f.required && '*'}</label>
                {f.multiline ? (
                  <textarea data-testid={`input-${f.key}`} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.label} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px]" />
                ) : (
                  <Input data-testid={`input-${f.key}`} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.label} />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button data-testid="save-client-btn" onClick={handleSave} size="sm"><Save className="h-4 w-4 mr-1" />Save</Button>
            <Button variant="outline" onClick={cancel} size="sm">Cancel</Button>
            {editing && (
              <Button
                data-testid="generate-access-code-btn"
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={async () => {
                  try {
                    const r = await fetch(`${API}/api/clients/${editing}/generate-access-code`, { method: 'POST', headers });
                    const d = await r.json();
                    if (d.success) { toast.success(`Access code: ${d.access_code}`); navigator.clipboard.writeText(d.access_code); fetchClients(); }
                  } catch { toast.error('Failed to generate code'); }
                }}
              >
                <Key className="h-4 w-4 mr-1" />Generate Extension Code
              </Button>
            )}
          </div>
          {editing && clients.find(c => c.client_id === editing)?.extension_access_code && (
            <div className="flex items-center gap-2 mt-1 p-2 rounded-lg bg-muted/20 border border-dashed">
              <Key className="h-3.5 w-3.5 text-orange-400" />
              <span className="text-xs text-muted-foreground">Extension Code:</span>
              <code className="text-sm font-mono font-bold text-orange-400">{clients.find(c => c.client_id === editing)?.extension_access_code}</code>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { navigator.clipboard.writeText(clients.find(c => c.client_id === editing)?.extension_access_code); toast.success('Copied!'); }}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="client-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or city..." className="pl-9" />
      </div>

      <div className="text-xs text-muted-foreground">{clients.length} clients</div>

      {loading ? <div className="text-center py-8 text-muted-foreground">Loading...</div> : (
        <div className="border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 border-b">
                  <th className="text-left p-3 font-medium">Name</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">City</th>
                  <th className="text-left p-3 font-medium hidden lg:table-cell">Province</th>
                  <th className="text-left p-3 font-medium hidden sm:table-cell">Email</th>
                  <th className="text-left p-3 font-medium hidden lg:table-cell">Phone</th>
                  <th className="p-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c, i) => {
                  const isExpanded = expanded[c.client_id];
                  const contacts = c.contacts || [];
                  return (
                    <React.Fragment key={c.client_id || i}>
                      <tr data-testid={`client-row-${i}`} className="border-b last:border-0 hover:bg-muted/10 transition-colors cursor-pointer" onClick={() => toggleExpand(c.client_id)}>
                        <td className="p-3 font-medium">
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                            <span>{c.name}</span>
                            {contacts.length > 0 && (
                              <span className="text-xs bg-muted/40 text-muted-foreground px-1.5 py-0.5 rounded-full">{contacts.length}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-muted-foreground hidden md:table-cell">{c.city}</td>
                        <td className="p-3 text-muted-foreground hidden lg:table-cell">{c.province}</td>
                        <td className="p-3 hidden sm:table-cell">{c.email && <a href={`mailto:${c.email}`} className="text-blue-400 hover:underline" onClick={e => e.stopPropagation()}>{c.email}</a>}</td>
                        <td className="p-3 text-muted-foreground hidden lg:table-cell">{c.phone}</td>
                        <td className="p-3" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => startEdit(c)} data-testid={`edit-client-${i}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => handleDeleteClient(c.id || c.client_id, c.name)} data-testid={`delete-client-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <>
                          {contacts.length > 0 && (
                            <tr className="bg-muted/5">
                              <td colSpan={6} className="p-0">
                                <table className="w-full">
                                  <thead>
                                    <tr className="border-b border-dashed">
                                      <th className="text-left p-2 pl-12 text-xs font-medium text-muted-foreground">Contact Name</th>
                                      <th className="text-left p-2 text-xs font-medium text-muted-foreground">Role</th>
                                      <th className="text-left p-2 text-xs font-medium text-muted-foreground">Email</th>
                                      <th className="text-left p-2 text-xs font-medium text-muted-foreground">Phone</th>
                                      <th className="p-2 w-20"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {contacts.map(ct => (
                                      <ContactRow key={ct.id} contact={ct} clientId={c.client_id} onUpdate={fetchClients} onDelete={fetchClients} />
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                          {addingContact === c.client_id && (
                            <tr className="bg-muted/5">
                              <td colSpan={6} className="p-0">
                                <table className="w-full">
                                  {contacts.length === 0 && (
                                    <thead>
                                      <tr className="border-b border-dashed">
                                        <th className="text-left p-2 pl-12 text-xs font-medium text-muted-foreground">Contact Name</th>
                                        <th className="text-left p-2 text-xs font-medium text-muted-foreground">Role</th>
                                        <th className="text-left p-2 text-xs font-medium text-muted-foreground">Email</th>
                                        <th className="text-left p-2 text-xs font-medium text-muted-foreground">Phone</th>
                                        <th className="p-2 w-20"></th>
                                      </tr>
                                    </thead>
                                  )}
                                  <tbody>
                                    <AddContactRow clientId={c.client_id} onAdded={() => { setAddingContact(null); fetchClients(); }} onCancel={() => setAddingContact(null)} />
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                          <tr className="bg-muted/5 border-b">
                            <td colSpan={6} className="p-2 pl-12">
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground" onClick={() => setAddingContact(c.client_id)} data-testid={`add-contact-${c.client_id}`}>
                                <UserPlus className="h-3 w-3 mr-1" />Add Contact
                              </Button>
                            </td>
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
